import { IContinueConditionState, WaitForGroup } from '../Basic/WaitForGroup'
import {ExecutionSerializer} from "../Basic/ExecutionSerializer";
import { BehaviorSubject } from 'rxjs'
import * as _ from "lodash";
import {PASearch} from "../PASearch";
import {esWB_Label} from "../PAdbSchema";

export interface ILabelTextCache {
    [sourceType: string]: { [languageCd: string]: {[labelType: string]: ILabelCacheEntry} }
}

export interface ILabelCacheEntry {
    sourceType: string
    languageCd: string
    labelType: string

    status: 'Init' | 'Queued' | 'Loaded'

    labels: { [wbId: number]: ILabel }

    // load Queue
    loadQueue: { [wbId: number]: number }
    waitForDataGroup: WaitForGroup<any>
}

export interface ILabel {
    Text: string,
    Tooltip: string,
    LabelType: string
}

export interface ILabelTextDataChangedEvent {
    SourceType: string
    LanguageCd: string
    LabelType: string
    Labels: ILabel[]
}

export class LabelTextService {
    private static dctCache: ILabelTextCache = {}
    private static qsProcessLoadQueue: ExecutionSerializer = new ExecutionSerializer(
        {},
        LabelTextService._LabelText_processQueue,
        [],
        500
    )
    private static viewSchema = PASearch.createSimpleSchema(
        ['SOURCE_TYPE', 'LABEL_TYPE_ID', 'LANGUAGE', 'TEXT', 'TOOLTIP'],
        ['SOURCE_WB_ID']
    )
    private static onDataChanged: BehaviorSubject<ILabelTextDataChangedEvent> =
        new BehaviorSubject<ILabelTextDataChangedEvent>(undefined)

    static get OnDataChanged(): BehaviorSubject<ILabelTextDataChangedEvent> {
        return LabelTextService.onDataChanged
    }

    //<editor-fold desc="Get Operations">

    static getLabel(sourceTypeId: string, sourceWbId: number, labelType: string, languageCd?: string): ILabel {
        if (_.isEmpty(sourceTypeId) || !_.isNumber(+sourceWbId)) return undefined

        if (_.isEmpty(labelType)) labelType = 'default'

        if (_.isEmpty(languageCd)) languageCd = 'en'

        let lce = LabelTextService.getOrCreateLabelCacheEntry(sourceTypeId, languageCd, labelType)
        if (sourceWbId in lce.labels) return _.clone(lce.labels[sourceWbId])

        lce.loadQueue[sourceWbId] = +sourceWbId
        if (lce.status != 'Queued') lce.status = 'Queued'
        LabelTextService.LabelText_processQueue()
    }

    static getLabelAsync(
        sourceTypeId: string,
        sourceWbId: number,
        labelType: string,
        languageCd?: string
    ): Promise<ILabel> {
        if (_.isEmpty(sourceTypeId) || !_.isNumber(+sourceWbId)) return Promise.resolve(undefined)

        if (_.isEmpty(labelType)) labelType = 'default'

        if (_.isEmpty(languageCd)) languageCd = 'en'

        let lt = LabelTextService.getLabel(sourceTypeId, sourceWbId, labelType, languageCd)
        if (lt) return Promise.resolve(lt)

        let lce = LabelTextService.getOrCreateLabelCacheEntry(sourceTypeId, languageCd, labelType)

        let rv = lce.waitForDataGroup
            .waitFor({}, (chkState: IContinueConditionState<any>) => {
                let bContinue = LabelTextService.isLabelLoaded(sourceTypeId, sourceWbId, labelType, languageCd)
                return bContinue
            })
            .then((ignore) => {
                let lbt = LabelTextService.getLabel(sourceTypeId, sourceWbId, labelType, languageCd)
                if(_.isUndefined(lbt) && (labelType !== 'default'))
                    return LabelTextService.getLabelAsync(sourceTypeId, sourceWbId, 'default', languageCd)
                return lbt
            })

        return rv
    }

    private static isLabelLoaded(
        sourceTypeId: string,
        sourceWbId: number,
        labelType: string,
        languageCd: string
    ): boolean {
        let ce = LabelTextService.getLabelCacheEntry(sourceTypeId, languageCd, labelType)
        if (_.isEmpty(ce)) return false

        return sourceWbId in ce.labels
    }

    private static getLabelCacheEntry(sourceTypeId: string, languageCd: string, labelType: string): ILabelCacheEntry {
        if (_.isEmpty(sourceTypeId) || _.isEmpty(languageCd) || _.isEmpty(labelType)) return undefined

        if (!(sourceTypeId in LabelTextService.dctCache)) return undefined
        let l2c = LabelTextService.dctCache[sourceTypeId]
        if (!(languageCd in l2c)) return undefined
        let l3c = l2c[languageCd]
        return l3c[labelType]
    }

    private static getOrCreateLabelCacheEntry(
        sourceTypeId: string,
        languageCd: string,
        labelType: string
    ): ILabelCacheEntry {
        let lce = LabelTextService.getLabelCacheEntry(sourceTypeId, languageCd, labelType)
        if (!_.isEmpty(lce)) return lce

        // Create Label Cache Entry
        if (!(sourceTypeId in LabelTextService.dctCache)) LabelTextService.dctCache[sourceTypeId] = {}

        let l2c = LabelTextService.dctCache[sourceTypeId]
        if (!(languageCd in l2c)) l2c[languageCd] = {}
        let l3c = l2c[languageCd]

        if (labelType in l3c) return l3c[labelType]

        l3c[labelType] = {
            sourceType: sourceTypeId,
            languageCd: languageCd,
            labelType: labelType,
            labels: {},
            status: 'Init',
            loadQueue: {},
            waitForDataGroup: new WaitForGroup<any>(LabelTextService)
        }

        return l3c[labelType]
    }

    //</editor-fold>

    private static LabelText_processQueue(): void {
        LabelTextService.qsProcessLoadQueue.Run({})
    }

    private static async _LabelText_processQueue(): Promise<void> {
        let bDebugQueueProcessing = true

        function logDebug(...args: any[]) {
            if (bDebugQueueProcessing) console.log(...args)
        }

        //--
        // Determine what needs to be loaded
        let queuedCEs: ILabelCacheEntry[] = []
        _.forEach(LabelTextService.dctCache, (l2c, sourceTypeId) => {
            _.forEach(l2c, (l3c, languageCd) => {
                _.forEach(l3c, (lce, labelType) => {
                    if (lce.status == 'Queued') queuedCEs.push(lce)
                })
            })
        })

        if (_.isEmpty(queuedCEs)) return
        // Load defaults first
        queuedCEs = _.orderBy(queuedCEs, (ce) => ce.labelType != 'default') // false sorts first

        let arrChangeEvents: ILabelTextDataChangedEvent[] = []

        for (let lce of queuedCEs) {
            try {
                let loadKeys = _.values(lce.loadQueue)
                let evt: ILabelTextDataChangedEvent = {
                    SourceType: lce.sourceType,
                    LabelType: lce.labelType,
                    LanguageCd: lce.languageCd,
                    Labels: []
                }
                // Use the Search Service to get the Data

                let bDefaultLabel = lce.labelType == 'default'
                let cnd: { [fld: string]: any } = {
                    SOURCE_TYPE: lce.sourceType,
                    LABEL_TYPE_ID: lce.labelType,
                    LANGUAGE: lce.languageCd,
                    SOURCE_WB_ID: {inq: loadKeys}
                }
                let results = await PASearch.find(
                    bDefaultLabel ? 'vUI_Label_Text_default' : esWB_Label,
                    cnd,
                    LabelTextService.viewSchema,
                    [],0,[])

                let dctResults = _.keyBy(results, (r) => r.SOURCE_WB_ID)
                // Add Labels to Dictionary
                _.forEach(loadKeys, (loadKey) => {
                    let r = dctResults[loadKey]
                    if(_.isUndefined(r))
                        lce.labels[loadKey] = undefined
                    else
                        lce.labels[loadKey] = {Text: r.TEXT || '', Tooltip: r.TOOLTIP || '', LabelType: lce.labelType}
                })

                // Remove items from Queue
                _.forEach(loadKeys, (wbId) => {
                    delete lce.loadQueue[wbId]
                })
                // If queue Empty, reset Queue status
                if (_.isEmpty(lce.loadQueue)) lce.status = 'Loaded'

                // Check Wait Group, flag Loaded
                lce.waitForDataGroup.checkContinueConditions()
                // Add Change Event
                if (!_.isEmpty(evt.Labels)) arrChangeEvents.push(evt)
            } catch (err) {
                console.log('LabelTextService: Exception: ', err)
            }
        }

        for (let evt of arrChangeEvents) {
            LabelTextService.OnDataChanged.next(evt)
        }
    } // End Load Process
}